import React, { useState } from 'react';
import DocumentListItem, { DocumentListItemProps } from './DocumentListItem';
import { NoResultsCard } from '@/components/Cards';
import { Trans } from '@lingui/react/macro';
import { BaseDocument } from '@/types';
import Dropzone from 'react-dropzone';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './DocumentList.module.scss';
import { DocumentListMode } from '.';
import { useCanScroll } from '@/utils/hooks';
import { GroupedMultiSelect, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import DocumentListSlideup from './DocumentListSlideup';

type ResidentialUnitDocumentListProps = Omit<DocumentListItemProps, 'document'> & {
    documents: BaseDocument[];
    onFileDrop?: (file: File, document: BaseDocument | null) => void;
    mode: DocumentListMode;
    defaultExpandedId?: string;
    selectedItems?: BaseDocument[];
    onSelectionChange?: (items: BaseDocument[]) => void;
    hideSelectAll?: boolean;
};

const DocumentList: React.FC<ResidentialUnitDocumentListProps> = ({
    documents,
    defaultExpandedId,
    onFileDrop,
    selectedItems: externalSelectedItems,
    onSelectionChange,
    hideSelectAll = false,
    ...rest
}) => {
    const canScroll = useCanScroll(defaultExpandedId);

    // Selection state management:
    // - Internal: Used when DocumentList is not grouped (e.g., project documents, single unit view)
    // - External: Used when managing selection across multiple lists (e.g., unit documents grouped by residential unit)
    // Example: Unit documents page renders multiple DocumentList instances, one per unit group,
    // with shared selection state to enable "Select all" across all groups
    const [internalSelectedItems, setInternalSelectedItems] = useState<BaseDocument[]>([]);
    const selectedItems = externalSelectedItems ?? internalSelectedItems;
    const setSelectedItems = onSelectionChange ?? setInternalSelectedItems;
    const handleDrop = (files: File[]) => {
        const file = files[0];

        if (file.size === 0) {
            Toast.error(<Trans>Cannot drag and drop files directly from compressed folders (.ZIP)</Trans>);
            return;
        }

        triggerHandler(onFileDrop, file, null);
    };

    const renderNoData = () => {
        const noDataMessage = (
            <NoResultsCard>
                <Trans>No documents</Trans>
            </NoResultsCard>
        );

        if (onFileDrop) {
            return (
                <Dropzone onDrop={handleDrop} noClick>
                    {({ getRootProps, isDragActive }) => (
                        <div
                            {...getRootProps()}
                            onClick={() => {}}
                            style={{ outline: 'none' }}
                            className={isDragActive ? theme.fileOver : ''}>
                            {noDataMessage}

                            <div className={theme.replaceDocumentOverlay}>
                                <Trans>Drop here to upload this document</Trans>
                            </div>
                        </div>
                    )}
                </Dropzone>
            );
        }

        return noDataMessage;
    };

    return (
        <>
            <GroupedMultiSelect
                ungroupedItems={documents}
                getItemId={(item: BaseDocument) => item.documentId}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                selectAllLabel={<Trans>Select all</Trans>}
                deselectAllLabel={<Trans>Select all</Trans>}
                showSelectAll={!hideSelectAll}
                renderItem={(item: BaseDocument) => (
                    <DocumentListItem
                        defaultExpanded={canScroll && defaultExpandedId === item.documentId}
                        key={item.documentId}
                        document={item}
                        onFileDrop={onFileDrop ? onFileDrop : undefined}
                        isSelected={selectedItems.includes(item)}
                        onCheckboxChange={() => {
                            setSelectedItems(
                                selectedItems.includes(item)
                                    ? selectedItems.filter((i) => i !== item)
                                    : [...selectedItems, item]
                            );
                        }}
                        {...rest}
                    />
                )}
            />

            {selectedItems.length > 0 && !externalSelectedItems && (
                <DocumentListSlideup
                    isOpen={true}
                    onCloseDialog={() => {
                        setSelectedItems([]);
                    }}
                    selectedDocuments={selectedItems}
                    onChange={() => rest.onChange?.([])}
                />
            )}
            {documents.length === 0 && renderNoData()}
        </>
    );
};

export default DocumentList;
